import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import FellowTable from '../../../components/tables/FellowTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useFellowSearch, useDeleteFellow } from '../../../hooks/useFellowApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { FellowDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { useProvinces } from '../../../hooks/useProvinceApi';

const FellowList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<any>({}); // Form input values
  const [items, setItems] = useState<FellowDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useFellowSearch();
  const deleteFellow = useDeleteFellow();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Fellows', href: '#' },
  ];

  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();

  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    showAdvanced,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSearchFilters,
    setShowAdvanced,
    commitSearch,
    clearSearchState,
  } = useSearchState('activities', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'Name',
        sortDescending: false,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Fellows:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleDelete = async (dto: FellowDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteFellow.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting Fellow:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/fellows"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Manage.Fellow.View'
    >
      <Card
        title="Manage Fellows"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAdvanced(!showAdvanced);
              }}
            >
              {showAdvanced ? 'Basic Search' : 'Advanced Search'}
            </Button>
            <AddNewButton onClick={() => navigate('/admin/fellows/create')} />
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="name"
                label="Search by Name"
                value={formFilters.name || ''}
                onChange={(e) => setFormFilters({ ...formFilters, name: e.target.value })}
                placeholder="Search by Name"
              />
              <FormField
                id="companyName"
                label="Search by Company Name"
                value={formFilters.companyName || ''}
                onChange={(e) => setFormFilters({ ...formFilters, companyName: e.target.value })}
                placeholder="Search by Company Name"
              />
              <FormField
                id="email"
                label="Search by Email"
                value={formFilters.email || ''}
                onChange={(e) => setFormFilters({ ...formFilters, email: e.target.value })}
                placeholder="Search by Email"
              />
              <FormField
                id="phoneNumber"
                label="Search by Phone Number"
                value={formFilters.phoneNumber || ''}
                onChange={(e) => setFormFilters({ ...formFilters, phoneNumber: e.target.value })}
                placeholder="Search by Phone Number"
              />
              <FormField
                id="provinceId"
                label="Search by Province"
                type='dropdown'
                value={formFilters.provinceId || ''}
                onChange={(e) => setFormFilters({ ...formFilters, provinceId: e.target.value })}
                placeholder="Select a Province"
                options={(provinces || []).map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingProvinces}
              />
            </div>
          )}


          <div className="flex space-x-4 mb-4">

            {showAdvanced && (
              <>
                <Button variant="secondary" onClick={handleClearSearch}>
                  Clear Search
                </Button>
                <Button type="submit">Search</Button>
              </>
            )}
          </div>
        </form>

        <FellowTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/fellows/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default FellowList;
